import { Directive, ElementRef, HostListener, Input, LOCALE_ID, Inject, forwardRef, OnInit, Optional, Self } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { formatNumber } from '@angular/common';

@Directive({
  selector: 'input[numericInputFormatter]', // Target input elements with this directive
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumericInputFormatterDirective),
      multi: true,
    },
  ],
})
export class NumericInputFormatterDirective implements ControlValueAccessor, OnInit {
  @Input('numericInputFormatterDecimalPlaces') decimalPlaces: number = 2;

  private _onChange: (value: number | null) => void = () => {};
  private _onTouched: () => void = () => {};
  private _isDisabled: boolean = false;
  private _value: number | null = null; // Internal storage for the model value

  constructor(
    private el: ElementRef<HTMLInputElement>,
    @Inject(LOCALE_ID) private locale: string,
  ) {
    // NgControl is no longer injected directly to avoid circular dependency.
    // Angular's forms module will link this CVA to the NgControl automatically.
  }

  ngOnInit(): void {
    // If the input type is "number", it will interfere with our formatting.
    // Console warn to suggest changing to type="text".
    if (this.el.nativeElement.type === 'number') {
      console.warn('numericInputFormatter Directive: Input type "number" might interfere with formatting. Consider changing to type="text" for this input.');
    }
  }

  // --- ControlValueAccessor methods ---

  // Writes a new value from the form model to the element.
  writeValue(value: number | null): void {
    this._value = value; // Update internal value
    // Format for display (always formatted when written from model)
    this.el.nativeElement.value = this._formatValue(value);
  }

  // Registers a callback function that is called when the control's value changes in the UI.
  registerOnChange(fn: (value: number | null) => void): void {
    this._onChange = fn;
  }

  // Registers a callback function that is called by the forms API when the control receives a blur event.
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  // This function is called by the forms API to update the control's disabled state.
  setDisabledState(isDisabled: boolean): void {
    this._isDisabled = isDisabled;
    this.el.nativeElement.disabled = isDisabled;
  }

  // --- Host Listeners for UI interaction ---

  @HostListener('input', ['$event.target.value'])
  onInput(value: string): void {
    if (this._isDisabled) return;

    // Clean value for internal parsing (remove grouping separators)
    const cleanedValue = this._cleanValueForParse(value);
    const parsedValue = parseFloat(cleanedValue);

    this._value = isNaN(parsedValue) ? null : parsedValue; // Update internal value immediately
    // Update the form control's model with the numeric value
    this._onChange(this._value);
  }

  @HostListener('focus')
  onFocus(): void {
    if (this._isDisabled) return;
    this._onTouched(); // Mark touched on focus for consistency with Angular Material inputs

    // On focus, display the raw, unformatted internal numeric value
    this.el.nativeElement.value = this._value !== null && this._value !== undefined
      ? String(this._value) // Display as string without formatting
      : '';
  }

  @HostListener('blur')
  onBlur(): void {
    if (this._isDisabled) return;
    this._onTouched(); // Mark touched on blur

    // On blur, get the current internal value (which reflects the model value)
    // and format it for display in the input field.
    this.el.nativeElement.value = this._formatValue(this._value);
  }

  // --- Helper Methods ---

  private _formatValue(value: number | null): string {
    if (value === null || value === undefined) {
      return '';
    }
    // Using Angular's formatNumber. `1.minDigits-maxDigits`
    // Set minFractionDigits to 0 to remove trailing .00, and maxFractionDigits to decimalPlaces.
    return formatNumber(value, this.locale, `1.0-${this.decimalPlaces}`);
  }

  private _cleanValueForParse(value: string): string {
    if (!value) return '';

    // Determine locale-specific separators using Intl.NumberFormat
    // This is a more reliable way to get separators for parsing
    const parts = new Intl.NumberFormat(this.locale).formatToParts(10000.50);
    const decimalSeparator = parts.find(p => p.type === 'decimal')?.value || '.';
    const thousandSeparator = parts.find(p => p.type === 'group')?.value || ',';

    let cleaned = value;

    // Remove thousand separators
    if (thousandSeparator && thousandSeparator !== '') {
      // Escape special regex characters in the separator
      const escapedThousandSeparator = thousandSeparator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      cleaned = cleaned.replace(new RegExp(escapedThousandSeparator, 'g'), '');
    }

    // Replace locale-specific decimal separator with a standard dot for parseFloat
    if (decimalSeparator && decimalSeparator !== '.') {
      const escapedDecimalSeparator = decimalSeparator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      cleaned = cleaned.replace(new RegExp(escapedDecimalSeparator, 'g'), '.');
    }

    // Remove any characters that are not digits, a decimal point, or a minus sign
    cleaned = cleaned.replace(/[^0-9.-]/g, '');

    // Ensure only one decimal point (e.g., "123.45.67" becomes "123.4567")
    const decimalParts = cleaned.split('.');
    if (decimalParts.length > 2) {
      cleaned = decimalParts.shift() + '.' + decimalParts.join('');
    }

    return cleaned;
  }
}
